import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import {
  skyFlightPointToWorld,
  skyFlightPointToLocal,
  skyFlightFrameFromTangent,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import { skyCathedralSweep } from '../../../packages/domain/src/99-sky-cathedral-space.mjs';

/** A readable third-person shoulder in Cathedral's actual free-flight basis.
 * The same frame owns control directions and cannon rays. Source solids shorten
 * the boom inside passages; an opposite shoulder is considered only if blocked.
 * This never scales artwork, moves an objective, or hides the pilot's helmet. */
export function skyCathedralCameraFrame(state, aspect) {
  const pose = state.cathedralPose ?? state.cathedral?.pose,
    basis = state.flightFrame;
  if (!pose?.valid || !basis) return null;
  const at = (x, y, z) => skyFlightPointToWorld(basis, { x, y, z });
  const eye = at(0, 0.3, 0),
    shoulder = aspect < 1 ? 0.7 : 1.25;
  const candidate = (side) => {
    const desired = at(side, 1.0, 5.85),
      hit = skyCathedralSweep(eye, desired, pose, 0.2);
    const fraction = hit ? Math.max(0, hit.fraction - 0.035) : 1;
    return {
      point: Object.fromEntries(
        ['x', 'y', 'z'].map((a) => [a, eye[a] + (desired[a] - eye[a]) * fraction]),
      ),
      fraction,
    };
  };
  let chosen = candidate(shoulder);
  if (chosen.fraction < 0.55) {
    const alternate = candidate(-shoulder);
    if (alternate.fraction > chosen.fraction) chosen = alternate;
  }
  if (chosen.fraction < 0.55) {
    const centre = candidate(0);
    if (centre.fraction > chosen.fraction) chosen = centre;
  }
  const look = at(0, 0.05, -12);
  const frame = {
    ...chosen.point,
    lookX: look.x,
    lookY: look.y,
    lookZ: look.z,
    up: { ...basis.up },
    fov: aspect < 1 ? 64 : 58,
    near: 0.12,
    firstPerson: false,
    maskWeight: 0,
  };
  const feet = at(0, SKY_WORLD.pilotSoleOffset - SKY_WORLD.pilotAimHeight, 0),
    helmet = at(
      0,
      SKY_WORLD.pilotSoleOffset - SKY_WORLD.pilotAimHeight + SKY_WORLD_ASSETS.pilot.height,
      0,
    );
  // The objective lies on the same physical flight bearing. A bounded angular
  // fit retains the pilot after the source geometry shortens a passage boom.
  const direction = { x: look.x - frame.x, y: look.y - frame.y, z: look.z - frame.z };
  const right = {
    x: direction.y * basis.up.z - direction.z * basis.up.y,
    y: direction.z * basis.up.x - direction.x * basis.up.z,
    z: direction.x * basis.up.y - direction.y * basis.up.x,
  };
  const cameraBasis = skyFlightFrameFromTangent(frame, direction, right);
  for (const point of [feet, helmet, state.viewTarget].filter(Boolean)) {
    const local = skyFlightPointToLocal(cameraBasis, point);
    if (local.z >= -0.1) continue;
    const required =
      (Math.atan(Math.max(Math.abs(local.x) / aspect, Math.abs(local.y)) / (-local.z * 0.88)) *
        360) /
      Math.PI;
    frame.fov = Math.max(frame.fov, Math.min(110, required));
  }
  return frame;
}

/** Highlight only the selected original rigid-skin conductor/knee surface.
 * Private materials preserve textures, opacity and geometry. The steady cue is
 * essential guidance and remains identical with reduced motion or paused time. */
export function createSkyCathedralGuidanceMaterial(T, mesh) {
  const joint = { value: -999 },
    colour = { value: new T.Color('#ffc654') };
  const names = new Map(mesh.skeleton.bones.map((bone, index) => [bone.name, index]));
  const originals = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const materials = originals.map((original) => {
    const material = original.clone(),
      prior = original.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      prior?.call(material, shader, renderer);
      shader.uniforms.skyGuidanceJoint = joint;
      shader.uniforms.skyGuidanceColour = colour;
      shader.vertexShader =
        'uniform float skyGuidanceJoint; varying float skyGuidanceMask;\n' + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <skinbase_vertex>',
        '#include <skinbase_vertex>\n skyGuidanceMask = 1.0 - step(0.5, abs(skinIndex.x - skyGuidanceJoint));',
      );
      shader.fragmentShader =
        'uniform vec3 skyGuidanceColour; varying float skyGuidanceMask;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
        '#include <emissivemap_fragment>\n totalEmissiveRadiance += skyGuidanceColour * skyGuidanceMask * 0.65;',
      );
    };
    material.customProgramCacheKey = () =>
      original.customProgramCacheKey() + '-cathedral-source-guidance-v1';
    return material;
  });
  mesh.material = Array.isArray(mesh.material) ? materials : materials[0];
  return {
    update(guidance) {
      joint.value = guidance?.ready ? (names.get('part__' + guidance.partId) ?? -999) : -999;
      colour.value.setHex(guidance?.kind === 'knee' ? 0x8ef5d8 : 0xffc654);
    },
    diagnostics: () => ({
      joint: joint.value,
      bone: mesh.skeleton.bones[joint.value]?.name ?? null,
    }),
    dispose() {
      for (const material of materials) material.dispose();
    },
  };
}
